import { CircleQuestionIcon, Typography } from "@codat/orchard-ui";
import { SourceTypeResponse, useManualIntegrationsQuery } from "api/company";
import classNames from "classnames";
import { ConfigStatusType, useConfig } from "contexts/ConfigContext";
import { SourceTypeStatus, useSourceTypes } from "contexts/SourceTypesContext";
import { useEffect, useMemo, useState } from "react";

import Button from "components/Button";
import DataSecurity from "components/DataSecurity";
import HelpModal from "components/HelpModal";
import LinkedAccounts from "components/LinkedAccounts";
import Logo from "components/Logo";
import { SecuredByCodat } from "components/SecuredByCodat";

import styles from "./LeftHandPane.module.css";

export interface LeftHandPaneProps {
  showLinkedAccounts?: boolean;
  currentSourceType?: SourceTypeResponse;
  isLoadingComplete: boolean;
}

export const LeftHandPane: React.FC<LeftHandPaneProps> = ({
  showLinkedAccounts,
  currentSourceType,
  isLoadingComplete,
}) => {
  const sourceTypeContext = useSourceTypes();
  const { state } = useConfig();
  const bankingSourceType = useMemo(
    () =>
      sourceTypeContext.sourceTypes?.find((s) => s.sourceType === "Banking"),
    [sourceTypeContext.sourceTypes]
  );
  const [
    { data: bankingIntegrationsData, loading: isBankingIntegrationsLoading },
    executeGetIntegrations,
  ] = useManualIntegrationsQuery(bankingSourceType?.integrationsUri ?? "");
  const bankingOnlyFileUpload =
    (bankingSourceType ?? false) &&
    (state.data.enableFileUpload?.banking ?? false) &&
    bankingIntegrationsData?.totalResults === 0;

  useEffect(() => {
    if (
      state.data.enableFileUpload?.banking &&
      bankingSourceType &&
      executeGetIntegrations
    ) {
      executeGetIntegrations();
    }
  }, [
    bankingSourceType,
    executeGetIntegrations,
    state.data.enableFileUpload?.banking,
  ]);

  const [isHelpModal, setHelpModal] = useState(false);

  const isComplete = useMemo(() => {
    const isSourceTypesReady = showLinkedAccounts
      ? sourceTypeContext && sourceTypeContext.status === SourceTypeStatus.READY
      : true;
    return (
      isSourceTypesReady &&
      state.status === ConfigStatusType.READY &&
      !isBankingIntegrationsLoading
    );
  }, [
    sourceTypeContext,
    showLinkedAccounts,
    state.status,
    isBankingIntegrationsLoading,
  ]);

  const { help, dataSecurity, logo, clientName, pageConfiguration } =
    state.data;

  const showHelp = help?.description || help?.link?.href || help?.link?.text;

  const showDataSecurity =
    dataSecurity?.description ||
    dataSecurity?.link?.href ||
    dataSecurity?.link?.text;

  const leftPanelTitle = pageConfiguration.leftPanel.title;
  const leftPanelBody = pageConfiguration.leftPanel.body;

  return (
    <div className={classNames(styles.pane, { [styles.show]: isComplete })}>
      <section>
        <div className={styles.topRow}>
          <Logo
            logo={logo}
            className={styles.logo}
            clientName={clientName}
            data-testid="l-pane-client-logo"
          />
          {showHelp && (
            <Button
              variant="tertiary"
              onClick={() => setHelpModal(true)}
              analyticsName="Open help"
              data-testid="l-pane-help"
            >
              <CircleQuestionIcon className={styles.icon} />
              <span>Help</span>
            </Button>
          )}
        </div>
        {leftPanelTitle && (
          <Typography variant="h1" as="h1" data-testid="left-panel-title">
            {leftPanelTitle}
          </Typography>
        )}
        {leftPanelBody && (
          <Typography
            as="p"
            className={styles.leftPanelBody}
            data-testid="left-panel-body"
          >
            {leftPanelBody}
          </Typography>
        )}
        {sourceTypeContext.sourceTypes && showLinkedAccounts && (
          <section
            className={classNames(styles.linkedAccountsSection, {
              [styles.show]: isLoadingComplete,
            })}
            data-testid="l-pane-linked-accounts"
          >
            <LinkedAccounts
              sourceTypes={sourceTypeContext.sourceTypes}
              currentSourceType={currentSourceType}
              bankingOnlyFileUpload={bankingOnlyFileUpload}
            />
          </section>
        )}
      </section>
      <section className={styles.lowerSection}>
        {showDataSecurity && (
          <section className={styles.dataSecuritySection}>
            <DataSecurity
              description={dataSecurity?.description}
              link={dataSecurity?.link}
              data-testid="l-pane-data-security"
            />
          </section>
        )}
        <div className={styles.securedByWrapper}>
          <SecuredByCodat />
        </div>
      </section>
      <HelpModal
        isOpen={isHelpModal}
        onClose={() => setHelpModal(false)}
        text={help?.description}
        link={help?.link}
      />
    </div>
  );
};
